import type { ReactNode } from "react";
import { Button } from "./Button";
import { cx } from "../utils/cx";

interface StateBaseProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  className?: string;
}

export interface EmptyStateProps extends StateBaseProps {
  action?: ReactNode;
  secondaryAction?: ReactNode;
  compact?: boolean;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  secondaryAction,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <section
      className={cx(
        "ui-state",
        "ui-empty-state",
        compact && "ui-state--compact",
        className,
      )}
    >
      {icon ? (
        <div aria-hidden="true" className="ui-state__icon">
          {icon}
        </div>
      ) : null}
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      {action || secondaryAction ? (
        <div className="ui-state__actions">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </section>
  );
}

export interface LoadingStateProps {
  label?: string;
  lines?: number;
  compact?: boolean;
  className?: string;
}

export function LoadingState({
  label = "Loading",
  lines = 4,
  compact = false,
  className,
}: LoadingStateProps) {
  return (
    <section
      role="status"
      aria-live="polite"
      aria-label={label}
      className={cx(
        "ui-state",
        "ui-loading-state",
        compact && "ui-state--compact",
        className,
      )}
    >
      <span className="ui-spinner" aria-hidden="true" />
      <span className="ui-visually-hidden">{label}</span>
      <div aria-hidden="true" className="ui-loading-state__lines">
        {Array.from({ length: Math.max(1, lines) }, (_, index) => (
          <span
            key={index}
            style={{ width: `${Math.max(42, 94 - index * 11)}%` }}
          />
        ))}
      </div>
    </section>
  );
}

export interface ErrorStateProps extends StateBaseProps {
  onRetry?: () => void;
  retryLabel?: string;
  details?: string;
  detailsLabel?: string;
  compact?: boolean;
  secondaryAction?: ReactNode;
}

export function ErrorState({
  title,
  description,
  icon = "!",
  onRetry,
  retryLabel = "Try again",
  details,
  detailsLabel = "Technical details",
  compact = false,
  secondaryAction,
  className,
}: ErrorStateProps) {
  return (
    <section
      role="alert"
      className={cx(
        "ui-state",
        "ui-error-state",
        compact && "ui-state--compact",
        className,
      )}
    >
      <div aria-hidden="true" className="ui-state__icon">
        {icon}
      </div>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
      {details ? (
        <details className="ui-error-state__details">
          <summary>{detailsLabel}</summary>
          <pre>{details}</pre>
        </details>
      ) : null}
      {onRetry || secondaryAction ? (
        <div className="ui-state__actions">
          {onRetry ? <Button onClick={onRetry}>{retryLabel}</Button> : null}
          {secondaryAction}
        </div>
      ) : null}
    </section>
  );
}

export type SaveStatusKind = "saved" | "saving" | "unsaved" | "error";

export interface SaveStatusProps {
  status: SaveStatusKind;
  label?: string;
  className?: string;
}

const saveStatusLabels: Record<SaveStatusKind, string> = {
  saved: "Saved",
  saving: "Saving…",
  unsaved: "Unsaved changes",
  error: "Could not save",
};

export function SaveStatus({
  status,
  label = saveStatusLabels[status],
  className,
}: SaveStatusProps) {
  return (
    <span
      role={status === "error" ? "alert" : "status"}
      aria-live={status === "saving" ? "polite" : "off"}
      className={cx(
        "ui-save-status",
        `ui-save-status--${status}`,
        className,
      )}
    >
      <span aria-hidden="true" className="ui-save-status__indicator" />
      <span>{label}</span>
    </span>
  );
}
